import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import AdminShell from "./AdminShell";
import { firestore } from "../firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";

/* ---------- UI ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
`;

const PillRow = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Pill = styled.button`
  appearance: none;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 950;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "rgba(15,118,110,0.10)" : "#fff")};
  color: ${({ $active }) => ($active ? "#0f766e" : "#111827")};

  &:hover {
    background: ${({ $active }) => ($active ? "rgba(15,118,110,0.12)" : "#f9fafb")};
    transform: translateY(-1px);
  }
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;

const Item = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 12px;
  background: #fff;
`;

const Top = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
`;

const Title = styled.div`
  font-weight: 950;
  color: #111827;
`;

const Meta = styled.div`
  color: #6b7280;
  font-weight: 850;
  font-size: 12px;

  a {
    color: #0f766e;
    text-decoration: none;
    font-weight: 950;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 12px;

  color: ${({ $type }) => ($type === "new" ? "#e94e1b" : "#0f766e")};
  background: ${({ $type }) =>
    $type === "new" ? "rgba(233,78,27,0.10)" : "rgba(15,118,110,0.10)"};
  border: 1px solid
    ${({ $type }) =>
      $type === "new" ? "rgba(233,78,27,0.22)" : "rgba(15,118,110,0.20)"};
`;

const Msg = styled.div`
  margin-top: 10px;
  color: #374151;
  font-weight: 750;
  line-height: 1.7;
  font-size: 14px;
  white-space: pre-wrap;
`;

const BtnRow = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }
`;

const Primary = styled(Btn)`
  border: 1px solid rgba(15,118,110,0.25);
  background: rgba(15,118,110,0.10);
  color: #0f766e;

  &:hover {
    filter: brightness(1.04);
  }
`;

const Danger = styled(Btn)`
  border-color: rgba(233,78,27,0.28);
  background: rgba(233,78,27,0.08);
  color: #e94e1b;
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 850;
  font-size: 13px;
`;

/* ---------- Helpers ---------- */

const topicLabel = (t) => {
  const map = {
    general: "Allmän fråga",
    booking: "Boka grupp / event",
    membership: "Medlemskap",
    sponsor: "Sponsring",
    error: "Fel på sidan",
  };
  return map[t] || t || "Okänt";
};

const toDateText = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : null;
    if (!d) return "—";
    return d.toLocaleString("sv-SE");
  } catch {
    return "—";
  }
};

/* ---------- Component ---------- */

export default function AdminContactMessages() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const [filter, setFilter] = useState("all"); // all | new | handled
  const [qText, setQText] = useState("");

  const load = async () => {
    const q = query(collection(firestore, "contact_messages"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = qText.trim().toLowerCase();
    return items
      .filter((it) => {
        if (filter === "new") return (it.status || "new") === "new";
        if (filter === "handled") return it.status === "handled";
        return true;
      })
      .filter((it) => {
        if (!s) return true;
        const hay = `${it.name || ""} ${it.email || ""} ${it.message || ""} ${topicLabel(
          it.topic
        )}`.toLowerCase();
        return hay.includes(s);
      });
  }, [items, filter, qText]);

  const stats = useMemo(() => {
    const total = items.length;
    const news = items.filter((x) => (x.status || "new") === "new").length;
    const handled = items.filter((x) => x.status === "handled").length;
    return { total, news, handled };
  }, [items]);

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      await updateDoc(doc(firestore, "contact_messages", id), { status });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort meddelandet?")) return;
    setBusy(true);
    try {
      await deleteDoc(doc(firestore, "contact_messages", id));
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title="Kontaktmeddelanden">
      <Grid>
        <Card>
          <h3 style={{ marginTop: 0 }}>Filter</h3>

          <SearchRow>
            <Input
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              placeholder="Sök på namn, e-post, ämne eller text…"
            />
          </SearchRow>

          <PillRow>
            <Pill type="button" $active={filter === "all"} onClick={() => setFilter("all")}>
              Alla ({stats.total})
            </Pill>
            <Pill type="button" $active={filter === "new"} onClick={() => setFilter("new")}>
              Nya ({stats.news})
            </Pill>
            <Pill type="button" $active={filter === "handled"} onClick={() => setFilter("handled")}>
              Hanterade ({stats.handled})
            </Pill>
            <Primary type="button" onClick={load} disabled={busy}>
              {busy ? "Uppdaterar…" : "Uppdatera"}
            </Primary>
          </PillRow>

          <Muted style={{ marginTop: 10 }}>
            Tips: Klicka “Hanterad” när du är klar med ett ärende.
          </Muted>
        </Card>

        <Card>
          <h3 style={{ marginTop: 0 }}>Lista</h3>

          {!filtered.length ? (
            <Muted>Inga meddelanden att visa.</Muted>
          ) : (
            <List>
              {filtered.map((it) => {
                const status = it.status || "new";
                return (
                  <Item key={it.id}>
                    <Top>
                      <div style={{ display: "grid", gap: 6 }}>
                        <Title>{topicLabel(it.topic)}</Title>
                        <Meta>
                          <div>
                            <b>{it.name || "Okänt namn"}</b> •{" "}
                            {it.email ? <a href={`mailto:${it.email}`}>{it.email}</a> : "—"}
                          </div>
                          <div>Skickat: {toDateText(it.createdAt)}</div>
                        </Meta>
                      </div>

                      <Badge $type={status}>{status === "new" ? "Ny" : "Hanterad"}</Badge>
                    </Top>

                    <Msg>{it.message || "—"}</Msg>

                    <BtnRow>
                      {status !== "handled" ? (
                        <Primary type="button" onClick={() => setStatus(it.id, "handled")} disabled={busy}>
                          Markera som hanterad
                        </Primary>
                      ) : (
                        <Btn type="button" onClick={() => setStatus(it.id, "new")} disabled={busy}>
                          Markera som ny
                        </Btn>
                      )}

                      <Danger type="button" onClick={() => remove(it.id)} disabled={busy}>
                        Ta bort
                      </Danger>
                    </BtnRow>
                  </Item>
                );
              })}
            </List>
          )}
        </Card>
      </Grid>
    </AdminShell>
  );
}
